import { Component, Host, h, Prop, Element, Method, Watch, State } from '@stencil/core';

import tippy, { inlinePositioning, Instance } from 'tippy.js';
import { TooltipColor, TooltipPlacement } from './tooltip-interface';
import { hasSlot } from '../../utils';

@Component({
  tag: 'o2-tooltip',
  styleUrl: 'tooltip.scss',
  shadow: false,
})
export class O2Tooltip {
  private tooltipInstance: Instance;
  private hasContentSlot: boolean = false;

  @Element() el: HTMLO2TooltipElement;

  @State() isInsideField: boolean = false;

  /**
   * Element reference. Id of other element.
   */
  @Prop() for?: string;

  /**
   * Set initial content for tooltip.
   */
  @Prop() content?: string;

  /**
   * Set initial placement of the tooltip.
   */
  @Prop() placement?: TooltipPlacement = 'top';

  /**
   * Show tooltip on init.
   */
  @Prop() open?: boolean = false;

  /**
   * If `true`, the user cannot interact with the input.
   */
  @Prop() disabled? = false;

  /**
   * Set offset on x axis to move tooltip left (- value) or right (value)
   * For o2-input, o2-textarea use value -126
   * For o2-datetime, o2-select use value -76
   */
  @Prop() offsetX?: number = 0;

  /**
   * Set offset on y axis to move tooltip top (- value) or bottom (value)
   * For o2-input, o2-textarea, o2-datetime, o2-select use value 15
   */
  @Prop() offsetY?: number = 15;

  /**
   * Color of the tooltip.
   * Possible values: `"brand"`, `"primary"`, `"success"`, `"warning"`, `"danger"`.
   */
  @Prop()
  color?: TooltipColor = 'brand';

  /**
   * Applies aria-hidden to the tooltip, when the tooltip information would have been duplicated.
   * For example when an icon button has a title or aria-label set.
   */
  @Prop() silent?: boolean = false;

  /**
   * Set content of the tooltip.
   */
  @Method()
  async setContent(text: string) {
    this.tooltipInstance.setContent(text);
  }

  /**
   * Show tooltip manually.
   */
  @Method()
  async show() {
    this.tooltipInstance.show();
  }

  /**
   * Hide tooltip manually.
   */
  @Method()
  async hide() {
    this.tooltipInstance.hide();
  }

  @Watch('disabled')
  protected disabledChanged(value: boolean) {
    if (this.tooltipInstance) {
      if (value) {
        this.tooltipInstance.disable();
      } else {
        this.tooltipInstance.enable();
      }
    }
  }

  private setOffsetToFormElements(tagName: string) {
    let offset = -1;

    switch (tagName) {
      case 'o2-input':
      case 'o2-textarea':
        offset = -136;
        break;
      case 'o2-datetime':
      case 'o2-select':
        offset = -108;
        break;
    }

    return offset;
  }

  connectedCallback() {
    this.hasContentSlot = hasSlot(this.el, '[slot="content"]');
  }

  componentDidLoad() {
    let elementForTooltip: Element;
    if (this.for) {
      elementForTooltip = this.el.parentElement.querySelector(`#${this.for}`);
    } else {
      elementForTooltip = this.el.querySelector('.o2-tooltip__element').firstElementChild;
    }

    const content = this.content ? this.content : this.el.querySelector('span[slot=content]');

    const parentTagName = this.el.parentElement.tagName.toLowerCase();
    const siblingElement = this.el.previousElementSibling || this.el.nextElementSibling;

    let offsetX = this.offsetX;
    let offsetY = this.offsetY;

    if (siblingElement) {
      const siblingTagName = siblingElement.tagName.toLowerCase();

      if (['o2-input', 'o2-datetime', 'o2-select', 'o2-textarea'].includes(siblingTagName)) {
        offsetX = this.setOffsetToFormElements(siblingTagName);
        offsetY = 15;
        this.isInsideField = true;
      }
    }

    if (['o2-input', 'o2-datetime', 'o2-select', 'o2-textarea'].includes(parentTagName)) {
      offsetX = this.setOffsetToFormElements(parentTagName);
      offsetY = 15;
      this.isInsideField = true;
    }

    this.tooltipInstance = tippy(elementForTooltip, {
      trigger: 'mouseenter focus',
      content: content,
      placement: this.placement,
      interactive: true,
      interactiveBorder: 10,
      allowHTML: true,
      appendTo: this.el.querySelector('.o2-tooltip__root'),
      inertia: true,
      animation: 'o2-tooltip',
      hideOnClick: false,
      maxWidth: 328,
      offset: [offsetX, offsetY],
      inlinePositioning: true,
      plugins: [inlinePositioning],
      onCreate: (instance: Instance) => {
        if (this.disabled) {
          instance.disable();
        }

        if (this.open) {
          instance.show();
        }

        // set aria-describedby
        elementForTooltip.setAttribute('aria-describedby', `tippy-${instance.id}`);
        if (!this.disabled) {
          elementForTooltip.setAttribute('tabindex', '0');
        }

        if (this.silent) {
          instance.popper.setAttribute('aria-hidden', 'true');
        }
      },
      onShow(instance: Instance) {
        // set role
        instance.popper.setAttribute('role', 'tooltip');
      },
    });
  }

  render() {
    return (
      <Host>
        <div class={`o2-tooltip o2-tooltip--${this.color} ${this.isInsideField ? 'o2-tooltip--inside-field' : ''}`}>
          <div class="o2-tooltip__root" part="tooltip"></div>
          <div class="o2-tooltip__element">
            <slot></slot>
          </div>
        </div>
        {this.hasContentSlot && <slot name="content"></slot>}
      </Host>
    );
  }
}
